import { prop, Vue } from 'vue-class-component';
import { VehicleResponse } from '@/types/writeup';
import { Button, ButtonTypes } from '@/components/button';
import { Icon, IconNames } from '@/components/icon';
import { ReserveVehicleWidget } from '@/components/views/customize-deal/components/reserve-vehicle-widget';
import { lockScroll, unlockScroll } from '@/common/helpers';
import { Transition } from 'vue';
import { Inject } from 'vue-property-decorator';
import type { VueI18n } from 'vue-i18n';
import { Carousel } from '@/components/carousel';
import { AMPLITUDE_LABELS } from '@/common/constants';
import { CoreModuleContext } from '@/store/core';
import styles from './styles.module.scss';

class VehicleInfoProps {
	vehicle = prop<VehicleResponse | void>({ required: true })

	loading = prop<boolean>({ default: false })

	images = prop<{ image: string }[]>({ default: [] })

	vin = prop<string | undefined>({ required: true })
}

export class VehicleInfo extends Vue.with(VehicleInfoProps) {
	@Inject() readonly i18n!: VueI18n;

	@Inject() readonly coreModule!: CoreModuleContext;

	expanded = false

	vehicleProperties = ['stockNumber', 'vin', 'exteriorColor', 'interiorColor', 'engine', 'transmission'];

	get vehicleTitle() {
		if (this.vehicle) {
			const { year, make, model, trim } = this.vehicle;

			return `${year} ${make} ${model} ${trim}`;
		}

		return this.i18n.t('vehicleInfo.titlePlaceholder');
	}

	get vehiclePrice() {
		return this.vehicle ? this.i18n.n(this.vehicle.price || 0, 'currency') : this.i18n.t('vehicleInfo.pricePlaceholder');
	}

	get vehicleInfo() {
		const epaCity = this.vehicle ? this.vehicle.epaCity : '0';
		const epaHighway = this.vehicle ? this.vehicle.epaHighway : '0';
		const data = this.vehicleProperties.map(property => {
			const name = this.i18n.t(`vehicleInfo.${property}`);

			return { name, value: this.vehicle ? String(this.vehicle[property]) : '' };
		});

		data.push({
			name: this.i18n.t('vehicleInfo.epaName'),
			value: this.i18n.t('vehicleInfo.epaValue', { city: epaCity, highway: epaHighway }),
		});

		return data;
	}

	get vehicleImage() {
		return this.vehicle ? this.vehicle.imageUrl : undefined;
	}

	handleExpand() {
		const eventData = {
			Label: AMPLITUDE_LABELS.vehicleInfo,
			Action: this.expanded ? 'CloseDetails' : 'ExpandDetails',
			...this.$route.meta,
		};

		this.coreModule.actions.sendEvent(eventData);
		this.expanded = !this.expanded;
	}

	openTransitionStart() {
		lockScroll(this.$refs.details as HTMLElement);
	}

	closeTransitionEnd() {
		unlockScroll(this.$refs.details as HTMLElement);
	}

	render() {
		const expandMoreButtonSlot = {
			icon: () => <Icon name={ IconNames.expandMore } size={ 30 }/>,
		};

		const expandLessButtonSlot = {
			icon: () => <Icon name={ IconNames.expandLess } size={ 30 }/>,
		};

		return (
			<div>
				<Transition
					onEnter={ this.openTransitionStart } onBeforeLeave={ this.closeTransitionEnd }
					name="vehicle-info_slide"
				>
					<div v-show={ this.expanded } class={ styles.vehicleInfoDetails } ref="details">
						<div class={styles.vehicleInfoImageWithBanner}>
							{ this.images.length > 0 && <Carousel slides={ this.images } fixedHeight = { '30vh' } progress={ false } /> }
							{ this.images.length === 0
							&& <img src={ this.vehicleImage } alt={ this.vehicle ? this.vehicleTitle : undefined } class={ styles.vehicleInfoImage }/>
							}
							<ReserveVehicleWidget class={ styles.vehicleInfoBadge }/>
						</div>
						<div class="precise-price-app__container">
							<div class={ styles.vehicleInfoDetailsTitle }>{ this.vehicleTitle }</div>
							<div class={ styles.vehicleInfoDetailsSubtitle }>{ this.vehiclePrice }</div>
							<div class={ styles.vehicleInfoTable }>
								<table>
									{ this.vehicleInfo.map(({ name, value }) => (
										<tr>
											<td>{ name }</td>
											<td>{ value }</td>
										</tr>
									)) }
								</table>
							</div>
							<Button
								class={ styles.vehicleInfoButton }
								fullWidth={ true }
								type={ ButtonTypes.link }
								onClick={ this.handleExpand }
								v-slots={ expandLessButtonSlot }>
								{ this.i18n.t('vehicleInfo.closeDetails') }
							</Button>
						</div>
					</div>
				</Transition>
				<div class={ styles.vehicleInfoBar }>
					<img src={ this.vehicleImage} alt={ this.vehicle ? this.vehicleTitle : undefined } class={ styles.vehicleInfoBackground }/>
					<div>
						<div class={ styles.vehicleInfoTitle }>{ this.vehicleTitle }</div>
						<div class={ styles.vehicleInfoSubtitle }>{ this.vehiclePrice }</div>
					</div>
					<Button type={ ButtonTypes.link } inverted onClick={ this.handleExpand } v-slots={ expandMoreButtonSlot }></Button>
				</div>
			</div>
		);
	}
}
